import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { PANTERA, LORAX, OLAF, OBSIDIAN, FLAT_EARTH, HEFFALUMP } from 'HubStyleTokens/colors';
import { TOOLBAR_HEIGHT_DARK } from 'HubStyleTokens/sizes';
import EPNavigationItem from './EPNavigationItem';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIButton from 'UIComponents/button/UIButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PrimaryDropdown = styled.div.withConfig({
  displayName: "EPNavigationDropdown__PrimaryDropdown",
  componentId: "sc-1nsodty-0"
})(["white-space:normal;& > button{cursor:pointer;display:flex;padding:0 1em;align-items:center;background-color:", ";line-height:", ";}.nav-2{background-color:", ";top:", ";line-height:1.5rem;left:0;min-width:190px;}.dropdown-list{padding:0;margin:0;}.expansion{z-index:9999;padding:1em 0;position:absolute;white-space:nowrap;}.nav-3{position:absolute;left:100%;margin-top:-52px;padding-top:13px;min-width:190px;background-color:", ";}"], props => props.isActiveItem && PANTERA, TOOLBAR_HEIGHT_DARK, PANTERA, TOOLBAR_HEIGHT_DARK, HEFFALUMP);
const NavigationDropdownSeparator = styled.div.withConfig({
  displayName: "EPNavigationDropdown__NavigationDropdownSeparator",
  componentId: "sc-1nsodty-1"
})(["border:1px solid ", ";margin:19px 17px;"], OBSIDIAN);
const NavigationItem = styled(EPNavigationItem).withConfig({
  displayName: "EPNavigationDropdown__NavigationItem",
  componentId: "sc-1nsodty-2"
})(["&&&{display:", ";align-items:center;padding:7px 17px;margin:1px 0 0 0;position:relative;width:100%;text-decoration:none;border-radius:0;.right-arrow-expand{padding-left:0.25em;margin-left:auto;margin-right:0;}&:hover,&:focus{background-color:", ";color:inherit;text-decoration:none;}}"], props => props.navItem.nestedChildren ? 'flex' : 'block', FLAT_EARTH);
const SubNavigation = ({
  level,
  navItems
}) => {
  const [nestedNavActiveItem, setNestedNavActiveItem] = useState('');
  return /*#__PURE__*/_jsx("div", {
    role: "menu",
    className: `nav-${level} expansion`,
    children: /*#__PURE__*/_jsx("ul", {
      role: "none",
      className: "dropdown-list",
      children: navItems.map((item, index) => {
        const {
          id,
          nestedChildren
        } = item;
        const key = `${id}-${index}`;
        if (id === 'separator') {
          return /*#__PURE__*/_jsx(NavigationDropdownSeparator, {
            role: "separator"
          }, key);
        }
        function handleNavItemClick() {
          setNestedNavActiveItem(id);
        }
        const navItem = Object.assign({}, item);

        // If an item has nestedChildren, we should be expanding the nav on click
        if (nestedChildren) {
          navItem.onClick = handleNavItemClick;
        }
        return /*#__PURE__*/_jsxs("div", {
          onMouseOver: handleNavItemClick,
          children: [/*#__PURE__*/_jsx(NavigationItem, {
            navItem: navItem
          }), !!nestedChildren && nestedNavActiveItem === id && /*#__PURE__*/_jsx(SubNavigation, {
            navItems: nestedChildren,
            level: level + 1
          })]
        }, key);
      })
    })
  });
};
const EPNavigationDropdown = ({
  activeItem,
  clearActiveItem,
  navGroup,
  setActiveItem
}) => {
  const {
    id: navGroupId,
    text,
    children
  } = navGroup;
  const [hovered, setHovered] = useState(false);
  const isActiveItem = activeItem === navGroupId;
  const handleSetActiveItem = useCallback(() => {
    setActiveItem(navGroupId);
  }, [navGroupId, setActiveItem]);
  const handleNavDropdownClick = useCallback(() => {
    setHovered(false);
    if (!isActiveItem) {
      handleSetActiveItem();
    } else {
      clearActiveItem();
    }
  }, [clearActiveItem, handleSetActiveItem, isActiveItem]);
  const handleMouseEnter = useCallback(() => {
    setHovered(true);
    if (activeItem) {
      handleSetActiveItem();
    }
  }, [activeItem, handleSetActiveItem]);
  const handleMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);
  return /*#__PURE__*/_jsxs(PrimaryDropdown, {
    role: "none",
    className: "primary-dropdown",
    "aria-haspopup": "true",
    "aria-expanded": isActiveItem,
    isActiveItem: isActiveItem,
    children: [/*#__PURE__*/_jsxs(UIButton, {
      onClick: handleNavDropdownClick,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      "data-nav-item": navGroupId,
      "data-test-id": navGroupId,
      use: "unstyled",
      children: [/*#__PURE__*/_jsx("span", {
        className: "p-x-2",
        children: text
      }), /*#__PURE__*/_jsx(UIIcon, {
        name: "down",
        color: isActiveItem || hovered ? LORAX : OLAF
      })]
    }), isActiveItem && !!children && /*#__PURE__*/_jsx(SubNavigation, {
      navItems: children,
      level: 2
    })]
  }, navGroupId);
};
EPNavigationDropdown.propTypes = {
  activeItem: PropTypes.string,
  navGroup: PropTypes.object.isRequired,
  onActiveItemChange: PropTypes.func
};
export default EPNavigationDropdown;